import { Flex, VStack } from '@web/components/atoms/Layout'
import { Section2 } from '@web/components/atoms/Typography'
import { DonationFormStep, ExtraFields, useDonationFormContext } from '@web/components/page-components/DonationForm'
import { useMemo, useState } from 'react'
import { ExtraField } from '@web/graphql-components'
import { PageButton } from '@web/components/page-components/PageButton'
import { DialogCloseTrigger, DialogContent, DialogRoot } from '@web/components/atoms/Modal'
import { DialogBackdrop } from '@web/components/chakra-ui/dialog'
import { PageProfileSelectorProps } from './ProfileSelector'
import { usePurchaseCopy } from '../../purchaseCopy'

type CallUsPopupFormProps = {
  charityName?: string
  campaignName?: string
  onSubmit?: () => Promise<void>
  Components: {
    ProfileSelector: React.FC<PageProfileSelectorProps>
  }
}

const CallUsPopupForm = ({ campaignName, onSubmit, Components }: CallUsPopupFormProps) => {
  const donationFormSettings = useDonationFormContext((state) => state.settings)
  const donationFormRef = useDonationFormContext((state) => state.donationFormRef)

  const { setStep } = useDonationFormContext(({ actions }) => actions)
  const [isCallUsModalOpen, setIsCallUsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const purchaseCopy = usePurchaseCopy()
  const fields: ExtraField[] = useMemo(
    () =>
      (donationFormSettings.styles?.options?.callUsFields as ExtraField[])
        ?.slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [donationFormSettings.styles?.options?.callUsFields],
  )

  const { options, ...styles } = donationFormSettings.styles || {}

  return (
    <>
      <DialogRoot open={isCallUsModalOpen} onOpenChange={(e) => setIsCallUsModalOpen(e.open)}>
        <DialogBackdrop backdropFilter="blur(2px)" zIndex="999" />
        <DialogContent portalRef={donationFormRef} css={styles}>
          <DialogCloseTrigger />
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (onSubmit) {
                setIsLoading(true)
                try {
                  await onSubmit()
                } finally {
                  setIsLoading(false)
                }
              }
              setIsCallUsModalOpen(false)
              setStep(DonationFormStep.ContactUsStep)
            }}
          >
            <VStack w="100%">
              <Section2>
                Thank you for your interest in supporting our {campaignName ?? ''}. Please complete this short form and
                we will be in contact with you.
              </Section2>
              <ExtraFields fields={fields} Components={Components} />
              <PageButton width="100%" type="submit" name="Primary" buttonText="Submit" isLoading={isLoading} />
            </VStack>
          </form>
        </DialogContent>
      </DialogRoot>
      <Flex w="100%" justifyContent="end">
        <PageButton
          name="Primary"
          buttonText={purchaseCopy('Call me to discuss my donation')}
          width="auto"
          size="xs"
          onClick={() => setIsCallUsModalOpen(true)}
        />
      </Flex>
    </>
  )
}

export default CallUsPopupForm
